
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useState } from "react";
import { ColumnMapping, ParsedQuote } from "@/types/import";
import { Trash } from "lucide-react";

interface CSVPreviewTableProps {
  data: ParsedQuote[];
  mapping: ColumnMapping;
  showDeletionWarning?: boolean;
}

export const CSVPreviewTable = ({ data, mapping, showDeletionWarning }: CSVPreviewTableProps) => {
  const [displayData, setDisplayData] = useState<ParsedQuote[]>([]);

  useEffect(() => {
    // Only show first 10 rows for preview
    setDisplayData(data.slice(0, 10));
  }, [data]);

  if (!displayData.length) {
    return <div className="text-center py-8">No data to preview</div>;
  }

  return (
    <div className="border rounded-md">
      <ScrollArea className="h-[500px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Row</TableHead>
              <TableHead>Content</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Categories</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayData.map((row, idx) => (
              <TableRow key={idx} className={row.toDelete ? "bg-red-50" : undefined}>
                <TableCell className="font-medium">{idx + 1}</TableCell>
                <TableCell className="max-w-xs truncate">
                  {row.content || "—"}
                </TableCell>
                <TableCell>{row.author || "—"}</TableCell>
                <TableCell>{row.source || "—"}</TableCell>
                <TableCell>
                  {row.toDelete ? (
                    <Badge variant="destructive" className="flex items-center">
                      <Trash className="mr-1 h-3 w-3" />
                      Will be deleted
                    </Badge>
                  ) : row.categories && row.categories.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {row.categories.map((category, i) => (
                        <Badge key={i} variant="outline">
                          {category}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  {row.validationErrors && row.validationErrors.length > 0 ? (
                    <Badge variant="destructive">Error</Badge>
                  ) : row.toDelete ? (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      Delete
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Valid
                    </Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
      {data.length > 10 && (
        <div className="p-2 text-center text-sm text-muted-foreground border-t">
          Showing 10 of {data.length} rows
        </div>
      )}
      
      {showDeletionWarning && data.some(row => row.toDelete) && (
        <div className="p-2 text-center text-sm text-red-600 border-t">
          Warning: {data.filter(row => row.toDelete).length} quote(s) will be deleted. This action cannot be undone.
        </div>
      )}
    </div>
  );
};
